import { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { nonNullable, take } from 'lib/array/arrayUtils'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { rem } from 'polished'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import type { MonthDayView } from 'selectors/offerPage/offerPageSelectors'
import { getCalendarDays, getDaysOtherAvailability } from 'selectors/offerPage/offerPageSelectors'
import OfferBundleAndSaveCalendarSidebarDaysMonth from './OfferBundleAndSaveCalendarSidebarDaysMonth'
import TextButton from 'LuxKit/Button/TextButton'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineFireIcon from 'LuxKit/Icons/line/LineFireIcon'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'

import { useIntl } from 'react-intl'
import productBadgeMessages from 'i18n/productBadgeMessages'

const Root = styled.div`
  width: 100%;
`

const Controls = styled.div`
  display: flex;

  > * + * {
    margin-inline-start: ${rem(12)};
  }
`

const NextMonthControls = styled(Controls)`
  margin-top: ${rem(16)};
`

const Calendar = styled.div`
  & + & {
    margin-top: ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

interface Props {
  daysByMonth: Array<MonthDayView>
  className?: string
  checkIn?: string
  onBackToMonth?: () => void
  room?: App.PackageRoomType
  initialMonth?: string
  otherAvailability: { [checkIn: string]: boolean }
  defaultDayPicker?: boolean
  flightsOnly?: boolean
  displayMemberPrice: boolean
}

function OfferBundleAndSaveCalendarSidebarDayPicker(props: Props) {
  const {
    onBackToMonth,
    daysByMonth,
    className,
    room,
    checkIn,
    initialMonth,
    otherAvailability,
    flightsOnly,
    defaultDayPicker,
    displayMemberPrice,
  } = props
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const [monthsOnDisplay, setMonthsOnDisplay] = useState(2)
  const [monthOffset, setMonthOffset] = useState(daysByMonth.findIndex((entry) => entry[1][0]?.monthKey === initialMonth))

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onPrevious = useCallback(() => {
    setMonthOffset(monthOffset - 1)
    setMonthsOnDisplay(monthsOnDisplay + 1)
    onUserAnalyticsAction?.('previous')
  }, [monthOffset, monthsOnDisplay, onUserAnalyticsAction])

  const onNext = useCallback(() => {
    setMonthsOnDisplay(monthsOnDisplay + 1)
    onUserAnalyticsAction?.('next')
  }, [monthsOnDisplay, onUserAnalyticsAction])

  const selectedDay = useMemo(() => {
    if (checkIn) {
      const checkInMoment = moment(checkIn)
      const key = `${checkInMoment.month()}-${checkInMoment.year()}`
      const monthDays = daysByMonth.find(entry => entry[0] === key)
      if (monthDays) {
        return nonNullable(monthDays[1]).find(day => day.checkIn === checkIn)
      }
    }
  }, [checkIn, daysByMonth])

  const limitedTimeSpecialLabel = intl.formatMessage(productBadgeMessages.limitedTimeSpecial)
  const previousMonthLabel = intl.formatMessage({
    id: 'wzAtSeVdhjO1ohNeIgWY',
    defaultMessage: 'Previous month',
  })
  const nextMonthLabel = intl.formatMessage({
    id: 'lvDLlZ4bNzn-aJ-jgWVC',
    defaultMessage: 'Next month',
  })
  const returnToYearlyCalendarLabel = intl.formatMessage({
    id: 'IFQWq24v1tmPc-R1Od8m',
    defaultMessage: 'Return to Yearly Calendar',
  })

  let FireIcon
  if (isLPCTacticalEnabled()) {
    FireIcon = <BodyTextBlock variant="medium" startIcon={<SolidFireIcon colour="urgency" />}>
      {limitedTimeSpecialLabel}
    </BodyTextBlock>
  } else {
    FireIcon = <BodyTextBlock variant="medium" startIcon={<LineFireIcon colour="highlight-primary-dark" />} format="titlecase">
      {intl.formatMessage({
        id: '_8MIW8cg3XhGnKMkYj9R',
        defaultMessage: 'Extra inclusions available',
      })}
    </BodyTextBlock>
  }

  return (
    <Root className={className}>
      {take(daysByMonth, monthsOnDisplay, monthOffset).map(([key, days, { year, monthIndex, tacticalDays }], index) => {
        const title = intlDateFormatter(new Date(year, monthIndex), INTL_LONG_MONTH_LONG_YEAR)

        return <Calendar key={key}>
          <Group direction="horizontal" verticalAlign="end" horizontalAlign="space-between">
            <Group direction="vertical" horizontalAlign="start" gap={4}>
              <BodyText variant="large" weight="bold">{title}</BodyText>
              {displayMemberPrice && <BodyText variant="medium" colour="lux-plus" weight="bold">
                {intl.formatMessage(
                  {
                    id: 'nrEKGq4y9LnbfmlbkzGJ',
                    defaultMessage: 'Showing {programName} member prices.',
                  },
                  { programName: LUXURY_PLUS.PROGRAM_NAME },
                )}
              </BodyText>}
              {tacticalDays.length > 0 && FireIcon}
            </Group>
            {index === 0 && <Controls>
              <TextButton
                kind="secondary"
                disabled={monthOffset === 0}
                onClick={onPrevious}
              >
                {previousMonthLabel}
              </TextButton>
              {!defaultDayPicker && <IconButton
                kind="secondary"
                onClick={onBackToMonth}
                aria-label={returnToYearlyCalendarLabel}
              >
                <LineCalendarIcon />
              </IconButton>}
            </Controls>}
          </Group>
          <OfferBundleAndSaveCalendarSidebarDaysMonth
            days={days}
            tacticalDays={tacticalDays}
            flightsOnly={flightsOnly}
            selectedDay={selectedDay}
            otherAvailability={otherAvailability}
            isRoomSelected={!!room}
          />
        </Calendar>
      })}
      <NextMonthControls>
        <TextButton
          kind="secondary"
          disabled={(monthsOnDisplay + monthOffset) >= daysByMonth.length}
          onClick={onNext}
        >
          {nextMonthLabel}
        </TextButton>
        <IconButton
          kind="secondary"
          onClick={onBackToMonth}
          aria-label={returnToYearlyCalendarLabel}
        >
          <LineCalendarIcon />
        </IconButton>
      </NextMonthControls>
    </Root>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    offer: state.offer,
    duration: state.duration,
    room: state.room,
    checkIn: state.checkInDate,
    daysByMonth: getCalendarDays(appState, state),
    otherAvailability: getDaysOtherAvailability(appState, state),
    displayMemberPrice: state.displayMemberPrice,
  }
}, OfferBundleAndSaveCalendarSidebarDayPicker)
